import {
  Box,
  Button,
  Link,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {useEffect, useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {formatSalaryInLacs} from '@jobseeker/lotus/utils';
import {formatPrettyDate, capitalizeWords} from '@utils';
import {extService} from '@jobseeker/common-core/services';

import {
  EDUCATION__TYPE_OPTIONS,
  MONTHS,
  GENDERS,
  NOTICE_PERIOD,
  INDUSTRIES,
  FUNCTIONAL_AREAS,
  ORGANISATION,
  DEGREES,
  LOCATIONS,
  HIRIST_FUNCTIONAL_AREAS,
  INDUSTRIES_HIRIST,
  HIRIST_INSTITUTES,
  HIRISTORGANISATION,
} from '@jobseeker/feature-onboarding/constants';

import {openUrl} from '@jobseeker/common-core/utils/common';
import {
  CoFields,
  EdFields,
  WeFields,
  PdFields,
  Recommendation,
  SocialInfo,
} from '@jobseeker/common-core/services/types';
import axios from 'axios';

const ABS_URL = 'https://static.iimjobs.com/';

interface MiddleProfileSectionProps {
  educationalDetails: EdFields[];
  certificationDetails: CoFields[];
  professionalDetails: WeFields[];
  moreDetails: PdFields | undefined;
  recommendations: Recommendation[];
  handleApproveRecommendation: (
    recommendationId: number,
    action: string
  ) => Promise<number>;
  handleRejectRecommendation: (
    recommendationId: number,
    action: string
  ) => Promise<number>;
}

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const MiddleProfileSection = ({
  educationalDetails,
  certificationDetails,
  professionalDetails,
  moreDetails,
  recommendations,
  handleApproveRecommendation,
  handleRejectRecommendation,
}: MiddleProfileSectionProps) => {
  const dob = moreDetails?.dob ? formatPrettyDate(moreDetails.dob) : '';
  const gender = moreDetails?.gender
    ? GENDERS.find(g => g.value === moreDetails.gender)?.label
    : '';
  const currentLocation = moreDetails?.currentLocation
    ? LOCATIONS.find(l => l.value.toString() === moreDetails.currentLocation)
        ?.label
    : '';
  const preferredLocations = moreDetails?.preferredLocations?.length
    ? moreDetails.preferredLocations
        .map(
          (locId: string) =>
            LOCATIONS.find(loc => loc.value.toString() === locId)?.label
        )
        // .filter(Boolean) // remove undefined if any id doesn't match
        .join(', ')
    : '';
  const noticePeriod = moreDetails?.noticePeriod
    ? NOTICE_PERIOD.find(np => np.value === moreDetails.noticePeriod)?.label
    : '';
  const industry = moreDetails?.industry
    ? (isAppIimjobs ? INDUSTRIES : INDUSTRIES_HIRIST).find(
        i => i.value === moreDetails.industry
      )?.label
    : '';
  const functionalArea = moreDetails?.functionalArea
    ? (isAppIimjobs ? FUNCTIONAL_AREAS : HIRIST_FUNCTIONAL_AREAS).find(
        fa => fa.value === moreDetails.functionalArea
      )?.label
    : '';
  const currentSalary = moreDetails?.currentSalary
    ? moreDetails.currentSalary
    : '';
  const expectedSalary = moreDetails?.expectedSalary
    ? moreDetails.expectedSalary
    : '';
  const currectCtc = moreDetails?.currentCtc
    ? moreDetails?.currentCtc !== 0
      ? moreDetails?.currentCtc + ' INR (lakhs/year)'
      : ''
    : '';
  const expectedCtc = moreDetails?.expectedCtc
    ? moreDetails?.expectedCtc + ' INR (lakhs/year)'
    : '';

  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommList2, setRecommList2] = useState<Recommendation[]>([]);
  const [socialInfoDetails, setSocialInfoDetails] = useState<SocialInfo[]>([]);

  useEffect(() => {
    if (recommendations && recommendations.length > 0) {
      const filtered = [...recommendations]
        .filter(r => r.status === 1 || r.status === 2 || r.status === 4)
        .reverse();
      setRecommList2(filtered);
      // console.log('recoml2: ', filtered);
    }
  }, [recommendations]);

  const isLarge = useIsLargeDevice(768);

  const onHideRecommendation = async (
    recommendationId: number,
    action: string
  ) => {
    const res = await handleRejectRecommendation(recommendationId, action);

    if (res === 2) {
      setRecommList2(prevList =>
        prevList.filter(r => r.id !== recommendationId)
      );
    }
  };

  const onApproveRecommendation = async (
    recommendationId: number,
    action: string
  ) => {
    const res = await handleApproveRecommendation(recommendationId, action);

    if (res === 2) {
      setRecommList2(prevList =>
        prevList.map(r => (r.id === recommendationId ? {...r, status: 2} : r))
      );
    }
  };

  useEffect(() => {
    async function fetchDetails() {
      try {
        const results = await Promise.all(
          recommList2.map(async r => {
            const res = await extService.getSocialInfo({
              email: r?.email,
              options: {
                headers: {
                  Authorization: `Bearer ${r?.key}`,
                },
              },
            });
            const socialInfo = res.data;
            return socialInfo;
          })
        );
        setSocialInfoDetails(results);
      } catch (err) {
        console.error('Failed to load recommendation info', err);
      }
    }
    if (recommList2?.length > 0) {
      fetchDetails();
    }
  }, [recommList2]);

  const [clusterOptions, setClusterOptions] = useState<
    {value: string; label: string}[]
  >([]);

  const loadInstitutes = async () => {
    try {
      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_STATIC_ASSESTS}/lists/institutes.json`
      );
      const institutes = response.data.institutes || [];
      const transformedInstitutes = institutes.map(
        (inst: {id: number; name: string}) => ({
          value: inst.id.toString(),
          label: inst.name,
        })
      );
      setClusterOptions(transformedInstitutes);
    } catch (err) {
      console.error('Error loading institutes:', err);
    }
  };

  useEffect(() => {
    if (isAppIimjobs) {
      loadInstitutes();
    }
  }, []);

  return (
    <Box
      sx={{
        p: isLarge ? '20px 25px' : '10px 15px',
      }}
    >
      <Box sx={{pb: '30px', width: '100%', float: 'left'}}>
        <List
          sx={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            width: '100%',
            float: 'left',
            display: 'flex',
          }}
        >
          <ListItem
            sx={{
              borderBottom: `3px solid ${
                showRecommendations ? '#e2e8d3' : '#888'
              }`,
              fontSize: {xxs: 12, md: 14.5},
              padding: '12px',
              textAlign: 'center',
              width: '50%',
              color: showRecommendations ? '#999' : '#4b4b4b',
              backgroundColor: showRecommendations ? '' : '#f7f7f7',
              transition: '0.1s',
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setShowRecommendations(false)}
          >
            Profile
          </ListItem>
          <ListItem
            sx={{
              borderBottom: `3px solid ${
                showRecommendations ? '#888' : '#e2e8d3'
              }`,
              fontSize: {xxs: 12, md: 14.5},
              padding: '12px',
              textAlign: 'center',
              width: '50%',
              color: showRecommendations ? '#4b4b4b' : '#999',
              backgroundColor: showRecommendations ? '#f7f7f7' : '',
              transition: '0.1s',
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setShowRecommendations(true)}
          >
            Recommendations
          </ListItem>
        </List>
      </Box>
      <Box sx={{display: showRecommendations ? 'none' : 'block'}}>
        <Box sx={{width: '100%', mb: '25px', float: 'left'}}>
          <Typography
            sx={{
              color: '#4b4b4b',
              fontSize: 18,
              fontWeight: 400,
              p: '11px 40px 11px 45px',
              background: `url(${ABS_URL}resources/img/prodetnew1.png) 0px  no-repeat`,
              wordBreak: 'break-all',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            Professional
            <span style={{color: '#999', marginLeft: '4px'}}>Details</span>
            <Link
              href="registration/addWorkExperience"
              target="_blank"
              style={{marginLeft: 'auto', marginRight: '-29px'}}
            >
              <Image
                src={`${ABS_URL}resources/img/editprofnew1.png`}
                alt="edit"
                width={12}
                height={12}
              />
            </Link>
          </Typography>
          {professionalDetails && professionalDetails.length > 0 && (
            <List
              sx={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                float: 'left',
                width: '100%',
              }}
            >
              {professionalDetails.map(
                (professionalDetail: WeFields, index: number) => {
                  const organisation =
                    (
                      (isAppIimjobs ? ORGANISATION : HIRISTORGANISATION) as {
                        label: string;
                        value: string | number;
                      }[]
                    ).find(
                      org =>
                        org.value.toString() === professionalDetail.organization
                    )?.label || professionalDetail.organization;
                  let isPresent = 0;
                  if (
                    professionalDetail.toExpMonth === 0 ||
                    professionalDetail.toExpYear === 0 ||
                    professionalDetail.isCurrent === 1
                  )
                    isPresent = 1;

                  return (
                    <ListItem
                      key={index}
                      sx={{
                        borderBottom:
                          index === professionalDetails.length - 1
                            ? 'none'
                            : '1px solid #ececec',
                        padding: '16px 0px',
                      }}
                      data-testid="professional-details"
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          color: '#4b4b4b !important',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#4b4b4b',
                            m: 0,
                            p: '0 0 5px',
                            fontSize: 14,
                            float: 'none',
                            fontWeight: 400,
                          }}
                        >
                          <span style={{color: '#4b4b4b', fontSize: 14}}>
                            {capitalizeWords(
                              professionalDetail.designation ?? ''
                            )}
                          </span>
                          {professionalDetail.isCurrent === 1 && (
                            <Tooltip
                              placement="top"
                              title={
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    textAlign: 'center',
                                    whiteSpace: 'normal',
                                    overflowWrap: 'break-word',
                                    backgroundColor: '#1c2d41',
                                    color: '#fff',
                                    padding: '0px',
                                    borderRadius: '4px',
                                    fontSize: 10,
                                    fontWeight: 400,
                                  }}
                                >
                                  Currently working
                                </Typography>
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#1c2d41',
                                    color: '#fff',
                                    p: '4px',
                                  },
                                },
                                arrow: {
                                  sx: {
                                    color: '#1c2d41',
                                  },
                                },
                              }}
                            >
                              <Image
                                src={`${ABS_URL}resources/img/tickigrey.png`}
                                alt="tick"
                                width={12}
                                height={10}
                                style={{marginLeft: '2px'}}
                              />
                            </Tooltip>
                          )}
                        </Typography>
                        <Typography sx={{m: 0, fontSize: 11.8}}>
                          <span style={{display: 'block'}}>{organisation}</span>
                          <span style={{display: 'block'}}>
                            {professionalDetail.fromExpMonth &&
                              MONTHS[professionalDetail.fromExpMonth - 1]
                                .label}{' '}
                            {professionalDetail.fromExpYear} to{' '}
                            {isPresent
                              ? 'Present'
                              : `${
                                  professionalDetail.toExpMonth &&
                                  MONTHS[professionalDetail.toExpMonth - 1]
                                    .label
                                } ${professionalDetail.toExpYear}`}{' '}
                          </span>
                        </Typography>
                      </Box>
                    </ListItem>
                  );
                }
              )}
            </List>
          )}
          <Button
            sx={{
              background: '#ebebeb',
              border: 0,
              color: '#4b4b4b',
              fontSize: 13,
              p: '7px 25px',
              textShadow: 'none',
              mt: '20px',
              '&:hover': {
                background: '#ebebeb',
              },
            }}
            onClick={() => openUrl('registration/addWorkExperience')}
          >
            Add Professional Details
          </Button>
        </Box>

        <Box sx={{width: '100%', mb: '25px', float: 'left'}}>
          <Typography
            sx={{
              color: '#4b4b4b',
              fontSize: 18,
              fontWeight: 400,
              p: '11px 40px 11px 45px',
              background: `url(${ABS_URL}resources/img/eddetnew1.png) 0px  no-repeat`,
              wordBreak: 'break-all',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            Educational
            <span style={{color: '#999', marginLeft: '4px'}}>Details</span>
            <Link
              href="registration/addEducationalDetails"
              target="_blank"
              style={{marginLeft: 'auto', marginRight: '-29px'}}
            >
              <Image
                src={`${ABS_URL}resources/img/editprofnew1.png`}
                alt="edit"
                width={12}
                height={12}
              />
            </Link>
          </Typography>
          {educationalDetails && educationalDetails.length > 0 && (
            <List
              sx={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                float: 'left',
                width: '100%',
              }}
            >
              {educationalDetails.map((edu: EdFields, index: number) => {
                const institute = (
                  (isAppIimjobs ? clusterOptions : HIRIST_INSTITUTES) || []
                ).find(inst => inst.value === edu.institute);
                const instituteLabel = institute
                  ? institute.label
                  : edu.institute;
                const educationType = EDUCATION__TYPE_OPTIONS.find(
                  type => type.value === edu.courseType?.toString()
                );
                const educationTypeLabel = educationType
                  ? educationType.label
                  : '';
                const degree = DEGREES.find(deg => deg.value === edu.degree);
                const degreeLabel = degree ? degree.label : '';
                return (
                  <ListItem
                    key={index}
                    sx={{
                      padding: '16px 0px',
                      borderBottom:
                        index === educationalDetails.length - 1
                          ? 'none'
                          : '1px solid #ececec',
                    }}
                    data-testid="educational-details-list"
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        color: '#4b4b4b !important',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#4b4b4b',
                          m: 0,
                          p: '0 0 5px',
                          fontSize: 14,
                          float: 'none',
                          fontWeight: 400,
                        }}
                      >
                        <span>
                          {degreeLabel} {`(${educationTypeLabel})`}
                        </span>
                        {edu.isPrimary === 1 && (
                          <Tooltip
                            placement="top"
                            title={
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  textAlign: 'center',
                                  whiteSpace: 'normal',
                                  overflowWrap: 'break-word',
                                  backgroundColor: '#1c2d41',
                                  color: '#fff',
                                  padding: '2px',
                                  borderRadius: '4px',
                                  fontSize: 10,
                                  fontWeight: 400,
                                }}
                              >
                                Most Relevant Education
                              </Typography>
                            }
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: '#1c2d41',
                                  color: '#fff',
                                  p: '4px',
                                },
                              },
                              arrow: {
                                sx: {
                                  color: '#1c2d41',
                                },
                              },
                            }}
                          >
                            <Image
                              src={`${ABS_URL}resources/img/tickigrey.png`}
                              alt="tick"
                              width={12}
                              height={10}
                              style={{marginLeft: '2px'}}
                            />
                          </Tooltip>
                        )}
                      </Typography>
                      <Typography sx={{m: 0, fontSize: 11.8}}>
                        <span style={{display: 'block'}}>{instituteLabel}</span>
                        <span style={{display: 'block'}}>
                          {edu.batchFrom} - {edu.batchTo}
                        </span>
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
          <Button
            sx={{
              background: '#ebebeb',
              border: 0,
              color: '#4b4b4b',
              fontSize: 13,
              p: '7px 25px',
              textShadow: 'none',
              mt: '20px',
              '&:hover': {
                background: '#ebebeb',
              },
            }}
            onClick={() => openUrl('registration/addEducationalDetails')}
          >
            Add Education Details
          </Button>
        </Box>

        <Box sx={{width: '100%', mb: '25px', float: 'left'}}>
          <Typography
            sx={{
              color: '#4b4b4b',
              fontSize: 18,
              fontWeight: 400,
              p: '11px 40px 11px 45px',
              background: `url(${ABS_URL}resources/img/certification.png) 0px  no-repeat`,
              wordBreak: 'break-all',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            Certification
            <span style={{color: '#999', marginLeft: '4px'}}>Details</span>
            <Link
              href="registration/addEducationalDetails"
              target="_blank"
              style={{marginLeft: 'auto', marginRight: '-29px'}}
            >
              <Image
                src={`${ABS_URL}resources/img/editprofnew1.png`}
                alt="edit"
                width={12}
                height={12}
              />
            </Link>
          </Typography>
          {certificationDetails && certificationDetails.length > 0 && (
            <List
              sx={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                float: 'left',
                width: '100%',
              }}
            >
              {certificationDetails.map(
                (certificate: CoFields, index: number) => {
                  const institute = (
                    (isAppIimjobs ? clusterOptions : HIRIST_INSTITUTES) || []
                  ).find(inst => inst.value === certificate.institute);
                  const instituteLabel = institute
                    ? institute.label
                    : certificate.institute;
                  return (
                    <ListItem
                      key={index}
                      sx={{
                        borderBottom:
                          index === certificationDetails.length - 1
                            ? 'none'
                            : '1px solid #ececec',
                        padding: '16px 0px',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          color: '#4b4b4b !important',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#4b4b4b',
                            m: 0,
                            p: '0 0 5px',
                            fontSize: 14,
                            float: 'none',
                            fontWeight: 400,
                          }}
                        >
                          {certificate.course}
                        </Typography>
                        <Typography sx={{m: 0, fontSize: 11.8}}>
                          <span style={{display: 'block'}}>
                            {instituteLabel}
                          </span>
                          <span style={{display: 'block'}}>
                            {certificate.fromExpMonth &&
                              MONTHS[certificate.fromExpMonth - 1].label}{' '}
                            {certificate.fromExpYear} -{' '}
                            {certificate.toExpMonth &&
                              MONTHS[certificate.toExpMonth - 1].label}{' '}
                            {certificate.toExpYear}
                          </span>
                        </Typography>
                      </Box>
                    </ListItem>
                  );
                }
              )}
            </List>
          )}
          <Button
            sx={{
              background: '#ebebeb',
              border: 0,
              color: '#4b4b4b',
              fontSize: 13,
              p: '7px 25px',
              textShadow: 'none',
              mt: '20px',
              '&:hover': {
                background: '#ebebeb',
              },
            }}
            onClick={() => openUrl('registration/addEducationalDetails')}
          >
            Add Certification Details
          </Button>
        </Box>
        {
          <Box sx={{width: '100%', mb: '25px', float: 'left'}}>
            <Typography
              sx={{
                color: '#4b4b4b',
                fontSize: 18,
                fontWeight: 400,
                p: '11px 40px 11px 45px',
                background: `url(${ABS_URL}resources/img/morredetnew1.png) 0px  no-repeat`,
                wordBreak: 'break-all',
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              More
              <span style={{color: '#999', marginLeft: '4px'}}>Details</span>
              <Link
                href="registration/addPersonalDetails"
                target="_blank"
                style={{marginLeft: 'auto', marginRight: '-29px'}}
              >
                <Image
                  src={`${ABS_URL}resources/img/editprofnew1.png`}
                  alt="edit"
                  width={12}
                  height={12}
                />
              </Link>
            </Typography>
            <Box
              sx={{display: 'flex', flexDirection: isLarge ? 'row' : 'column'}}
            >
              <Box sx={{width: isLarge ? '50%' : '100%', position: 'relative'}}>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Date of Birth:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-all',
                        fontSize: 13,
                      }}
                    >
                      {dob}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Gender:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-all',
                        fontSize: 13,
                      }}
                    >
                      {gender}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Current Location:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-all',
                        fontSize: 13,
                      }}
                    >
                      {currentLocation}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Preferred Location:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-word',
                        fontSize: 13,
                      }}
                    >
                      {preferredLocations}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Notice period:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-all',
                        fontSize: 13,
                      }}
                    >
                      {noticePeriod}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{width: isLarge ? '50%' : '100%', position: 'relative'}}>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Industry:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-all',
                        fontSize: 13,
                      }}
                    >
                      {industry}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Functional Area:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-word',
                        fontSize: 13,
                      }}
                    >
                      {functionalArea}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Annual Current Salary:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-word',
                        fontSize: 13,
                      }}
                    >
                      {isAppIimjobs
                        ? formatSalaryInLacs(Number(currentSalary))
                        : currectCtc}{' '}
                      {moreDetails?.confidential === 1 && ` | Confidential`}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex'}}>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#4b4b4b',
                        wordBreak: 'break-all',
                        fontSize: 13,
                        mb: '5px',
                      }}
                    >
                      Annual Expected Salary:
                    </Typography>
                  </Box>
                  <Box sx={{width: '50%'}}>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: '#999',
                        wordBreak: 'break-word',
                        fontSize: 13,
                      }}
                    >
                      {isAppIimjobs
                        ? formatSalaryInLacs(Number(expectedSalary))
                        : expectedCtc}{' '}
                      {moreDetails?.negotiable === 1 && ` | Negotiable`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        }
      </Box>
      <Box sx={{display: showRecommendations ? 'block' : 'none'}}>
        {recommList2 && recommList2.length > 0 ? (
          <List
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              float: 'left',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {recommList2.map((r, index) => {
              return (
                <ListItem
                  key={index}
                  sx={{
                    borderBottom: '1px solid #ececec',
                    p: '25px 0px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{width: '100%', display: 'flex', alignItems: 'center'}}
                  >
                    <Image
                      src={
                        socialInfoDetails[index]?.picUrl ||
                        `${ABS_URL}resources/img/user_profile_new.png`
                      }
                      alt="user-icon"
                      width={50}
                      height={50}
                      style={{
                        borderRadius: '50%',
                        marginRight: '15px',
                        float: 'left',
                        overflow: 'hidden',
                        transition: '1s',
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#4b4b4b',
                        fontSize: 16,
                        overflow: 'hidden',
                        p: '5px 0px 10px',
                        textAlign: 'left',
                      }}
                    >
                      {capitalizeWords(socialInfoDetails[index]?.name)}
                      <span
                        style={{
                          display: 'block',
                          fontSize: 15,
                          lineHeight: '16px',
                          padding: '5px 0px 0px',
                        }}
                      >
                        <Link
                          href={socialInfoDetails[index]?.socialUrl}
                          sx={{
                            color: '#4b4b4b',
                            textDecoration: 'none',
                            '&:hover': {textDecoration: 'underline'},
                          }}
                        >
                          {socialInfoDetails[index]?.socialUrl}
                        </Link>
                      </span>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <Typography sx={{p: 0, mb: 0, fontSize: 13, pt: '5px'}}>
                      {r?.recommendationText}
                    </Typography>
                    {r?.status === 1 ? (
                      <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                        <Button
                          sx={{
                            bgcolor: '#ebebeb',
                            color: '#4b4b4b',
                            fontSize: 13,
                            p: '7px 25px',
                            border: 0,
                            textShadow: 'none',
                            mt: '20px',
                            '&:hover': {bgcolor: '#ebebeb'},
                          }}
                          onClick={() =>
                            onApproveRecommendation(r?.id, 'approve')
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          sx={{
                            bgcolor: '#ebebeb',
                            color: '#4b4b4b',
                            fontSize: 13,
                            p: '7px 25px',
                            border: 0,
                            textShadow: 'none',
                            ml: '5px',
                            mt: '20px',
                            '&:hover': {bgcolor: '#ebebeb'},
                          }}
                          onClick={() => onHideRecommendation(r?.id, 'reject')}
                        >
                          Reject
                        </Button>
                      </Box>
                    ) : r?.status === 2 || r?.status === 4 ? (
                      <Typography>
                        <Button
                          sx={{
                            bgcolor: '#ebebeb',
                            color: '#4b4b4b',
                            fontSize: 13,
                            p: '7px 25px',
                            border: 0,
                            textShadow: 'none',
                            mt: '20px',
                            '&:hover': {bgcolor: '#ebebeb'},
                          }}
                          onClick={() => onHideRecommendation(r?.id, 'reject')}
                        >
                          Hide
                        </Button>
                      </Typography>
                    ) : (
                      ''
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography sx={{m: '0px 0px 10px', fontSize: 14, color: '#4b4b4b'}}>
            No Recommendation Yet
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MiddleProfileSection;
